import React, { useEffect, useState } from 'react';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import './dashboard.css';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import CryptoJS from 'crypto-js';
import { useDispatch } from 'react-redux'; // Import useDispatch
import { changepassword } from '../store/slices/userSlice';
import { unwrapResult } from '@reduxjs/toolkit'; // Import unwrapResult

function Changepassword() {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch(); // Initialize useDispatch
  const [userId, setUserId] = useState(null); // Renamed `id` to `userId` for clarity
  const [loading, setLoading] = useState(true);

  const [existingPassword, setExistingPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [showExisting, setShowExisting] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // Retrieve stored encrypted password from localStorage
  // In a real application, fetching this securely from the backend
  // for verification is highly recommended instead of localStorage.
  const storedEncryptedPassword = localStorage.getItem('password') || '';

  useEffect(() => {
    const localId = localStorage.getItem('id');
    if (localId) {
      setUserId(localId);
    }
    setLoading(false); // Stop loading once user ID is checked
  }, [searchParams]);

  const validateNewPassword = (password) => {
    // Password must be at least 8 characters long, and contain:
    // - at least one digit
    // - at least one letter (upper or lowercase)
    // - at least one special character
    const hasMinLength = password.length >= 8;
    const hasDigit = /\d/.test(password);
    const hasAlpha = /[a-zA-Z]/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    return hasMinLength && hasDigit && hasAlpha && hasSpecial;
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    // Verify existing password against the stored one
    const encryptedExisting = CryptoJS.SHA256(existingPassword).toString();
    if (encryptedExisting !== storedEncryptedPassword) {
      Swal.fire('Error', 'The existing password you entered is incorrect. Please try again.', 'error');
      return;
    }

    // Validate new password strength
    if (!validateNewPassword(newPassword)) {
      Swal.fire(
        'Error',
        'New password must be at least 8 characters long and include at least one digit, one letter, and one special character (!@#$%^&*(),.?":{}|<>).',
        'error'
      );
      return;
    }

    // Check if new password matches confirm password
    if (newPassword !== confirmPassword) {
      Swal.fire('Error', 'The new password and confirm password do not match!', 'error');
      return;
    }

    // Prevent changing to the same password
    const encryptedNewPasswordCheck = CryptoJS.SHA256(newPassword).toString();
    if (encryptedNewPasswordCheck === storedEncryptedPassword) {
        Swal.fire('Warning', 'Your new password cannot be the same as your existing password.', 'warning');
        return;
    }

    try {
      // Encrypt the new password before sending
      const encryptedPassword = CryptoJS.SHA256(newPassword).toString();
      const payload = { id: userId, password: encryptedPassword };

      // Dispatch the changepassword thunk and unwrap its result
      const actionResult = await (changepassword(payload));
      const response = (actionResult); // This will throw an error if the thunk was rejected

      // If unwrapResult did not throw, it means the request was successful
      if (response?.status === 200) {
        Swal.fire('Success', 'Your password has been changed successfully!', 'success');
        // Update the stored password in localStorage with the new encrypted password
        localStorage.setItem('password', encryptedPassword);
        // Clear form fields
        setExistingPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        // This block might be hit if the backend returns a non-200 status
        // but the thunk itself doesn't reject (less common with well-structured thunks)
        Swal.fire('Error', response?.message || 'Failed to change password. Please try again.', 'error');
      }
    } catch (error) {
      // Catch network errors, errors from thunk's rejectWithValue, or other unexpected issues
      console.error('Error changing password:', error);

      let errorMessageText = 'An unexpected error occurred while changing your password. Please try again.';

      if (error && error.message) {
        errorMessageText = error.message; // Error message from rejectWithValue
      } else if (error && error.response && error.response.data && error.response.data.message) {
        errorMessageText = error.response.data.message; // Common for Axios errors
      }

      Swal.fire('Error', errorMessageText, 'error');
    }
  };

  return (
    <div>
      <Navbar />
      <Sidebar />

      {loading ? (
        <div className="preloader" style={{ display: 'block' }}> {/* Ensure preloader is visible when loading */}
          <div className="lds-ripple">
            <div className="lds-pos"></div>
            <div className="lds-pos"></div>
          </div>
        </div>
      ) : (
        <div id="main-wrapper">
          <div className="page-wrapper d-block">
            <div className="body-wrapper">
              <div className="container-fluid">
                <h3 className="mb-4">Change Password</h3>
                <div className="card"> {/* Wrap form in a card for better styling */}
                  <div className="card-body">
                    <form onSubmit={handleResetPassword}>
                      {/* Existing Password */}
                      <div className="mb-3 position-relative">
                        <label htmlFor="existingPassword" className="form-label">Existing Password</label>
                        <input
                          type={showExisting ? 'text' : 'password'}
                          className="form-control"
                          id="existingPassword"
                          value={existingPassword}
                          onChange={(e) => setExistingPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowExisting(!showExisting)}
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2 p-0"
                          aria-label="Toggle Existing Password Visibility"
                        >
                          {showExisting ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
                        </button>
                      </div>

                      {/* New Password */}
                      <div className="mb-3 position-relative">
                        <label htmlFor="newPassword" className="form-label">New Password</label>
                        <input
                          type={showNew ? 'text' : 'password'}
                          className="form-control"
                          id="newPassword"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowNew(!showNew)}
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2 p-0"
                          aria-label="Toggle New Password Visibility"
                        >
                          {showNew ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
                        </button>
                        <small className="form-text text-muted">
                          Password must be at least 8 characters long and include at least one digit, one letter, and one special character.
                        </small>
                      </div>

                      {/* Confirm Password */}
                      <div className="mb-3 position-relative">
                        <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                        <input
                          type={showConfirm ? 'text' : 'password'}
                          className="form-control"
                          id="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirm(!showConfirm)}
                          className="btn btn-link position-absolute end-0 top-50 translate-middle-y me-2 p-0"
                          aria-label="Toggle Confirm Password Visibility"
                        >
                          {showConfirm ? <i className="fas fa-eye-slash"></i> : <i className="fas fa-eye"></i>}
                        </button>
                      </div>

                      <button type="submit" className="btn btn-primary">
                        Change Password
                      </button>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Changepassword;